import React, {useEffect, useMemo} from 'react';
import {Box, Flex, HStack, VStack, useTheme, Image, Icon} from '@chakra-ui/react';
import {TextG10, TextG12, TextG14, TextG16} from '../../typography';
import {generatePath, Link, useParams} from 'react-router-dom';
import {ROUTES} from '../../../constants/routes';
import {Place} from '../../../types/article';
import {calculateDistance, getDistanceString} from '../../../utils/distance';
import {useTrackVisibility} from 'react-intersection-observer-hook';
import useVisibleArticles from '../../../hooks/useVisible';
import useGetUserLocation from '../../../hooks/useUserLocation';
import styled from 'styled-components';
import {storeScrollPositionInSession} from '../../../utils/scroll';
import CardBadge from '../../CardBadge';
import FallbackImg from '../../../assets/images/fallback.jpg';

// SRM Teal color as per the branding requirements
const SRM_TEAL = '#00858C';

const CardContainer = styled(Box)`
  background: white;
  border-radius: 12px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  width: 100%;
`;

const CoverImage = styled(Image)`
  width: 100%;
  height: 180px;
  object-fit: cover;
`;

const AvailabilityDot = styled(Box)<{$isAvailableNow: boolean}>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props: {$isAvailableNow: boolean}) =>
    props.$isAvailableNow ? '#4CAF50' : '#F9CE39'};
  flex-shrink: 0;
`;

interface ApartmentInfo {
  bedrooms: number | string | null;
  bathrooms: number | string | null;
  availableDate: string | null;
  availableTBD: boolean;
  utilitiesIncluded: boolean;
  neighborhood: string | null;
  monthlyRent: number | null;
}

/**
 * Parse apartment-specific information from a Place object
 * The data may come from the `info` field or be embedded in other fields
 * Falls back to parsing the description HTML for embedded data
 */
const parseApartmentInfo = (place: Place): ApartmentInfo => {
  const info = (place as any).info || {};
  const metadata = info.metadata || {};
  const main = metadata.main || {};
  const secondary = metadata.secondary || {};
  const description = (place as any).description || (place as any).description_plain_text || '';

  // Parse bedrooms - could be in various formats
  let bedrooms: number | string | null = null;
  if (secondary.beds !== undefined) {
    bedrooms = secondary.beds;
  } else if (main.bedrooms !== undefined) {
    bedrooms = main.bedrooms;
  } else if (info.bedrooms !== undefined) {
    bedrooms = info.bedrooms;
  } else if (info.number_of_bedrooms !== undefined) {
    bedrooms = info.number_of_bedrooms;
  }

  // Parse bathrooms - check info field first, then fall back to description parsing
  let bathrooms: number | string | null = null;
  if (secondary.bathrooms !== undefined) {
    bathrooms = secondary.bathrooms;
  } else if (info.bathrooms !== undefined) {
    bathrooms = info.bathrooms;
  } else if (info.number_of_bathrooms !== undefined) {
    bathrooms = info.number_of_bathrooms;
  } else if (description) {
    // Try to parse from description (e.g., "1 Bathroom" or "2 Bathrooms")
    const bathroomMatch = description.match(/(\d+(?:\.\d+)?)\s*Bathroom/i);
    if (bathroomMatch) {
      bathrooms = bathroomMatch[1];
    }
  }

  // Parse available date - check info field first, then description
  let availableDate: string | null = null;
  let availableTBD = false;
  if (secondary.available_date) {
    availableDate = secondary.available_date;
  } else if (info.available_date) {
    availableDate = info.available_date;
  } else if (info.availability_date) {
    availableDate = info.availability_date;
  } else if (description) {
    // Try to parse from description (e.g., "First Look Starting: 02/15/26" or "First Look Starting: TBD")
    const firstLookMatch = description.match(/First Look Starting:\s*(?:<[^>]*>)*\s*(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
    if (firstLookMatch) {
      // Parse the date (format: MM/DD/YY or MM/DD/YYYY)
      const dateParts = firstLookMatch[1].split('/');
      if (dateParts.length === 3) {
        let year = parseInt(dateParts[2]);
        // Handle 2-digit year
        if (year < 100) {
          year = year + 2000;
        }
        availableDate = `${year}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
      }
    } else if (/First Look Starting:\s*(?:<[^>]*>)*\s*TBD/i.test(description)) {
      // "TBD" means coming soon but date unknown
      availableTBD = true;
    }
  }

  // Parse utilities included - check info field first, then description
  let utilitiesIncluded = false;
  if (main.utilities_included !== undefined) {
    utilitiesIncluded = !!main.utilities_included;
  } else if (info.utilities_included !== undefined) {
    utilitiesIncluded = !!info.utilities_included;
  } else if (info.sdge_included !== undefined) {
    utilitiesIncluded = !!info.sdge_included;
  } else if (description) {
    // Check if description mentions utilities/SDGE included
    utilitiesIncluded = /sdge|utilities\s*included/i.test(description);
  }

  // Parse neighborhood - check info field, then description, then city
  let neighborhood: string | null = null;
  if (secondary.neighborhood) {
    neighborhood = secondary.neighborhood;
  } else if (info.neighborhood) {
    neighborhood = info.neighborhood;
  } else if (place.city) {
    neighborhood = place.city;
  }

  // Parse monthly rent
  let monthlyRent: number | null = null;
  if (info.monthly_rent !== undefined) {
    monthlyRent = Number(info.monthly_rent);
  } else if (place.price) {
    // Try to parse price from string (e.g., "$1,895" or "1895")
    const priceMatch = place.price.toString().replace(/[$,]/g, '');
    const parsed = parseFloat(priceMatch);
    if (!isNaN(parsed)) {
      monthlyRent = parsed;
    }
  }

  return {
    bedrooms,
    bathrooms,
    availableDate,
    availableTBD,
    utilitiesIncluded,
    neighborhood,
    monthlyRent,
  };
};

/**
 * Determine if the apartment is available now or in the future
 */
const isAvailableNow = (availableDate: string | null, availableTBD: boolean): boolean => {
  if (availableTBD) return false; // TBD means coming soon
  if (!availableDate) return true; // Assume available if no date specified
  const date = new Date(availableDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date <= today;
};

/**
 * Format availability text
 */
const getAvailabilityText = (availableDate: string | null, availableTBD: boolean): string => {
  if (availableTBD) return 'Coming Soon';
  if (!availableDate) return 'Available Now';

  const date = new Date(availableDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (date <= today) {
    return 'Available Now';
  }

  // Format date as "Available Month Day"
  const options: Intl.DateTimeFormatOptions = {month: 'long', day: 'numeric'};
  return `Available ${date.toLocaleDateString('en-US', options)}`;
};

/**
 * Format bedroom label
 */
const getBedroomLabel = (bedrooms: number | string | null): string => {
  if (bedrooms === null || bedrooms === undefined) return '';
  const num = Number(bedrooms);
  if (num === 0) return 'Studio';
  if (num === 1) return '1';
  return String(num);
};

/**
 * Format price
 */
const formatPrice = (price: number | null): string => {
  if (price === null || price === undefined) return '';
  return `$${price.toLocaleString()}`;
};

const ApartmentCard = ({
  place,
  leftTopItem,
  rightTopItem,
  modal,
  isSlide = false,
  isPreview = false,
}: {
  place?: Place;
  leftTopItem?: React.ReactNode;
  rightTopItem?: React.ReactNode;
  height?: string;
  modal?: boolean;
  isSlide?: boolean;
  isPreview?: boolean;
}) => {
  if (!place) return null;

  const theme = useTheme();
  const {pathname} = useParams();
  const isPartner = !!(place.partner || place.partner_id);

  let userLocation: ReturnType<typeof useGetUserLocation> | undefined;
  if (!isPreview) {
    userLocation = useGetUserLocation();
  }

  const distance = useMemo(() => {
    if (place && userLocation?.data) {
      return calculateDistance(
        {
          latitude: Number(userLocation.data?.latitude),
          longitude: Number(userLocation.data?.longitude),
        },
        {
          latitude: Number(place.latitude),
          longitude: Number(place.longitude),
        }
      );
    }
    return 0;
  }, [userLocation?.data, place]);

  const [ref, {wasEverVisible}] = useTrackVisibility();
  const {addVisibleArticle} = useVisibleArticles();

  useEffect(() => {
    if (wasEverVisible && place?.id.toString()) {
      addVisibleArticle({id: place.id.toString(), type: 'place'});
    }
  }, [wasEverVisible]);

  const image = useMemo(() => {
    if (place) {
      return place.image_url || FallbackImg;
    }
    return FallbackImg;
  }, [place]);

  // Parse apartment-specific info
  const apartmentInfo = useMemo(() => parseApartmentInfo(place), [place]);
  const availableNow = useMemo(
    () => isAvailableNow(apartmentInfo.availableDate, apartmentInfo.availableTBD),
    [apartmentInfo.availableDate, apartmentInfo.availableTBD]
  );
  const availabilityText = useMemo(
    () => getAvailabilityText(apartmentInfo.availableDate, apartmentInfo.availableTBD),
    [apartmentInfo.availableDate, apartmentInfo.availableTBD]
  );

  // Parse the name to extract bedroom info if not in info field
  const displayBedrooms = useMemo(() => {
    if (apartmentInfo.bedrooms !== null) {
      return getBedroomLabel(apartmentInfo.bedrooms);
    }
    // Try to extract from name (e.g., "Studio - 4280 Campus Ave" or "2 Bedroom - ...")
    const name = place.name || place.title || '';
    if (name.toLowerCase().includes('studio')) return 'Studio';
    const bedroomMatch = name.match(/(\d+)\s*(bed|br)/i);
    if (bedroomMatch) return bedroomMatch[1];
    return null;
  }, [place.name, place.title, apartmentInfo.bedrooms]);

  // Get price from apartment info or place
  const displayPrice = useMemo(() => {
    if (apartmentInfo.monthlyRent) {
      return formatPrice(apartmentInfo.monthlyRent);
    }
    return place.price ? `$${place.price.toString().replace(/[$,]/g, '')}` : null;
  }, [apartmentInfo.monthlyRent, place.price]);

  // Extract address/unit from place
  const addressLine = useMemo(() => {
    const parts: string[] = [];
    if (place.street_address) parts.push(place.street_address);
    return parts.join(', ');
  }, [place]);

  const neighborhood = apartmentInfo.neighborhood || place.city || '';

  return (
    <Link
      style={{width: '100%', display: 'contents'}}
      to={
        isPreview
          ? `/place/${place.uuid}/preview`
          : modal
          ? {pathname, search: `?id=${place.id.toString()}&articleType=place`}
          : generatePath(ROUTES.PLACE, {
              placeId: place.id.toString(),
            })
      }
      onClick={() => storeScrollPositionInSession()}
      ref={ref}>
      <CardContainer
        mt={isSlide ? '1.25%' : 0}
        width={isSlide ? '97.5%' : '100%'}
        ml={isSlide ? '1.25%' : 0}>
        {/* Partner Badge */}
        {isPartner && (
          <CardBadge top="12px" w="145px" label="WALKABOUT PARTNER" center="true" />
        )}

        {/* Cover Image */}
        <Box position="relative">
          <CoverImage src={image} alt={place.name || 'Apartment'} fallbackSrc={FallbackImg} />
          {leftTopItem && (
            <Box position="absolute" top="8px" left="8px">
              {leftTopItem}
            </Box>
          )}
          {rightTopItem && (
            <Box position="absolute" top="8px" right="8px">
              {rightTopItem}
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box p={4}>
          {/* Availability Row */}
          <HStack spacing={2} mb={2}>
            <AvailabilityDot $isAvailableNow={availableNow} />
            <TextG12 color={theme.colors.grey} fontWeight={500}>
              {availabilityText}
            </TextG12>
          </HStack>

          {/* Price */}
          {displayPrice && (
            <TextG16 fontWeight={700} color={theme.colors.lightBlack} mb={1}>
              {displayPrice}/mo
            </TextG16>
          )}

          {/* Stats Row - Bedrooms, Bathrooms, Utilities */}
          <HStack spacing={4} mb={2} flexWrap="wrap">
            {/* Bedrooms */}
            {displayBedrooms !== null && (
              <HStack spacing={1}>
                <Box color={SRM_TEAL}>
                  <i
                    className="fi fi-rr-bed-alt"
                    style={{fontSize: '14px', display: 'flex', alignItems: 'center'}}
                  />
                </Box>
                <TextG12 color={theme.colors.lightBlack}>
                  {displayBedrooms === 'Studio' ? 'Studio' : displayBedrooms}
                </TextG12>
              </HStack>
            )}

            {/* Bathrooms */}
            {apartmentInfo.bathrooms !== null && (
              <HStack spacing={1}>
                <Box color={SRM_TEAL}>
                  <i
                    className="fi fi-rr-bath"
                    style={{fontSize: '14px', display: 'flex', alignItems: 'center'}}
                  />
                </Box>
                <TextG12 color={theme.colors.lightBlack}>{apartmentInfo.bathrooms}</TextG12>
              </HStack>
            )}

            {/* Utilities Included */}
            {apartmentInfo.utilitiesIncluded && (
              <HStack spacing={1}>
                <Box color={SRM_TEAL}>
                  <i
                    className="fi fi-rr-bolt"
                    style={{fontSize: '14px', display: 'flex', alignItems: 'center'}}
                  />
                </Box>
                <TextG12 color={theme.colors.lightBlack}>SDGE Utilities Included</TextG12>
              </HStack>
            )}
          </HStack>

          {/* Address */}
          {addressLine && (
            <TextG14 fontWeight={500} color={theme.colors.lightBlack} noOfLines={1}>
              {addressLine}
            </TextG14>
          )}

          {/* Neighborhood */}
          {neighborhood && (
            <TextG12 color={theme.colors.grey} mt={0.5}>
              {neighborhood}
            </TextG12>
          )}

          {/* Distance (if available) */}
          {distance > 0 && (
            <TextG10 color={theme.colors.grey} mt={1}>
              {getDistanceString(distance)}
            </TextG10>
          )}
        </Box>
      </CardContainer>
    </Link>
  );
};

export default ApartmentCard;
